<template>
  <v-dialog
    v-model="isModalOpen"
    max-width="980px"
    content-class="fixed-height-md"
  >
    <modal-card
      :title="modalTitle"
      :loading="loading"
      fixed
      full
      @close="closeModal"
    >
      <div class="file-viewer-container" v-if="isModalOpen">
        <!-- Audio Player -->
        <audio-player-pro
          v-if="isAudio"
          :src="currentFile?.cdn"
          :title="currentFile?.filename"
          class="audio-player"
        />

        <!-- Video Player -->
        <video-player
          v-else-if="isVideo"
          ref="player"
          :item="videoItem"
          :active="true"
          :popup="false"
          :taking="false"
          :testing="false"
          :options="clientPlayerOptions"
        />

        <!-- Image Viewer -->
        <div v-else-if="isImage" class="image-container">
          <img
            v-if="currentFile?.cdn"
            :src="currentFile.cdn"
            :alt="currentFile.filename || 'Image preview'"
          />
        </div>

        <!-- PDF and other files -->
        <div v-else class="file-container">
          <iframe
            v-if="previewUrl"
            :src="previewUrl"
            frameborder="0"
            title="Document preview"
          ></iframe>
        </div>
      </div>
    </modal-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex';
import EventBus from '@/factories/eventBus';
import AudioPlayerPro from '@/components/basic/audio-player-pro/AudioPlayerPro.vue';
import VideoPlayer from '@/views/lessons/lessonWidgets/videoWidget/VideoPlayer';

export default {
  name: 'UniversalFilesViewer',
  components: {
    AudioPlayerPro,
    VideoPlayer,
  },
  data() {
    return {
      isModalOpen: false,
      modalTitle: 'File Viewer',
      loading: false,
      file: null,
      clientPlayerOptions: {
        presentMode: true,
        autoPlay: true,
      },
    };
  },
  computed: {
    currentFile() {
      return this.file || {};
    },
    isAudio() {
      return this.matchesMimeType('audio');
    },
    isVideo() {
      return this.matchesMimeType('video');
    },
    isImage() {
      return this.matchesMimeType('image');
    },
    videoItem() {
      return {
        values: {
          url: this.getVideoUrl(),
          summary: {},
        },
      };
    },
    previewUrl() {
      const { cdn } = this.currentFile;
      if (!cdn) return '';

      if (cdn.includes('/preview/')) {
        return cdn;
      }

      if (!cdn.includes('filestack')) {
        return cdn;
      }

      const handle = cdn.split('/').pop();
      return `https://cdn.filestackcontent.com/preview/${handle}`;
    },
  },
  methods: {
    matchesMimeType(type) {
      return (this.currentFile.mimetype || '').includes(type);
    },
    openViewer(file) {
      this.file = file;
      this.isModalOpen = true;
    },
    closeModal() {
      this.isModalOpen = false;
      this.fileId = null;
    },
    getVideoUrl() {
      const { asset, url, cdn } = this.currentFile;
      if (asset) {
        const { playback_ids, from, security_token, video_id } = asset || {};
        if (playback_ids?.length) {
          const playbackId = asset.playback_ids[0].id;
          return `https://stream.mux.com/${playbackId}.m3u8`;
        }

        if (from === 'sprout' && video_id && security_token) {
          return `https://videos.sproutvideo.com/embed/${video_id}/${security_token}`;
        }
      }

      return cdn || url || '';
    },
  },
  mounted() {
    EventBus.$on('openFileViewer', this.openViewer);
  },
  beforeDestroy() {
    EventBus.$off('openFileViewer', this.openViewer);
  },
};
</script>

<style lang="less" scoped>
.file-viewer-container {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  .audio-player {
    width: 100%;
    max-width: 600px;
  }

  .player {
    height: 100%;
    width: 100%;
  }

  .image-container {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
      border-radius: 4px;
    }
  }

  .file-container {
    width: 100%;
    height: 100%;

    iframe {
      width: 100%;
      height: 100%;
      border: none;
    }
  }
}

:deep(.fixed-height-md) {
  height: 80vh;
  max-height: 800px;
}
</style>
